/**
 * AdminDriverPayouts — Payout-P3 admin board
 * Consumes:
 *   GET  /api/admin/driver-withdrawals (list + filters)
 *   POST /api/admin/driver-withdrawals/{id}/approve
 *   POST /api/admin/driver-withdrawals/{id}/reject
 *   POST /api/admin/driver-withdrawals/{id}/mark-paid
 *   GET  /api/admin/driver-earnings-digest (weekly sheet)
 *
 * Also provides CSV export of digest + collision-triage CSV export.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, Download, RefreshCw, Filter, CheckCircle, XCircle, CreditCard,
  Loader2, AlertTriangle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const fmtJMD = (v) => new Intl.NumberFormat('en-JM', { style: 'currency', currency: 'JMD', minimumFractionDigits: 0 }).format(v || 0);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-JM') : '—';

const STATUS_BADGE = {
  requested: 'bg-yellow-100 text-yellow-800',
  approved:  'bg-blue-100 text-blue-800',
  paid:      'bg-green-100 text-green-800',
  rejected:  'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-700',
};

function downloadCSV(rows, filename) {
  if (!rows || !rows.length) return;
  const headers = Object.keys(rows[0]);
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [headers.join(','), ...rows.map(r => headers.map(h => esc(r[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
}

export default function AdminDriverPayouts() {
  const navigate = useNavigate();
  const token = (typeof window !== 'undefined' ? (localStorage.getItem('token') || sessionStorage.getItem('token')) : '') || '';
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const [status, setStatus] = useState('');
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(null);
  const [digest, setDigest] = useState(null);
  const [collisions, setCollisions] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const q = status ? `?status=${status}` : '';
      const [wr, dg, co] = await Promise.all([
        fetch(`${API_URL}/api/admin/driver-withdrawals${q}`, { headers: authHeaders }),
        fetch(`${API_URL}/api/admin/driver-earnings-digest`,   { headers: authHeaders }),
        fetch(`${API_URL}/api/admin/identity/collisions?limit=500`, { headers: authHeaders }),
      ]);
      if (!wr.ok) throw new Error('withdrawals load failed');
      setWithdrawals((await wr.json()).withdrawals || []);
      if (dg.ok) setDigest(await dg.json());
      if (co.ok) setCollisions((await co.json()).collisions || []);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  useEffect(() => { if (token) load(); /* eslint-disable-next-line */ }, [token, status]);

  const act = async (id, kind) => {
    let body = {};
    if (kind === 'reject') {
      const reason = window.prompt('Rejection reason? (min 3 chars)');
      if (!reason || reason.trim().length < 3) { toast.error('Reason required'); return; }
      body = { reason: reason.trim() };
    } else if (kind === 'mark-paid') {
      const payment_reference = window.prompt('Payment reference? (e.g. NCB-REF-12345)');
      if (!payment_reference || payment_reference.trim().length < 3) { toast.error('Reference required'); return; }
      const payment_method = window.prompt('Payment method (bank_transfer / cash_office):', 'bank_transfer') || 'bank_transfer';
      body = { payment_reference: payment_reference.trim(), payment_method };
    }
    setActioning(`${id}:${kind}`);
    try {
      const r = await fetch(`${API_URL}/api/admin/driver-withdrawals/${id}/${kind}`, {
        method: 'POST', headers: authHeaders, body: JSON.stringify(body),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || `${kind} failed`);
      toast.success(`${kind} ok`);
      load();
    } catch (e) { toast.error(e.message); } finally { setActioning(null); }
  };

  const exportDigestCSV = () => {
    if (!digest?.rows) return;
    const rows = digest.rows.map(r => ({
      driver_id:                r.driver_id,
      name:                     r.name || '',
      driver_type:              r.driver_type || '',
      phone:                    r.primary_phone_normalized || '',
      period_earnings_jmd:      r.period_earnings_jmd ?? 0,
      direct_jmd:               r.direct_jmd ?? 0,
      transit_jmd:              r.transit_jmd ?? 0,
      period_paid_jmd:          r.period_paid_jmd ?? 0,
      available_jmd:            r.wallet?.available_jmd ?? 0,
      pending_jmd:              r.wallet?.pending_jmd ?? 0,
      current_balance_jmd:      r.wallet?.current_balance_jmd ?? 0,
      lifetime_earned_jmd:      r.wallet?.lifetime_earned_jmd ?? 0,
      lifetime_withdrawn_jmd:   r.wallet?.lifetime_withdrawn_jmd ?? 0,
      next_release_date:        r.wallet?.next_release_date || '',
    }));
    downloadCSV(rows, `driver_payout_digest_${digest.week_start?.slice(0, 10)}_${digest.week_end?.slice(0, 10)}.csv`);
  };

  const exportCollisionsCSV = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/identity/collisions/export-csv`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('export failed');
      const text = await r.text();
      const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'identity_collisions.csv';
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const markReviewed = async (auditId) => {
    const note = window.prompt('Review note (optional)') || '';
    try {
      const r = await fetch(`${API_URL}/api/admin/identity/collisions/${auditId}/mark-reviewed`, {
        method: 'POST', headers: authHeaders, body: JSON.stringify({ note }),
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || 'mark-reviewed failed'); }
      toast.success('Marked reviewed');
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="admin-driver-payouts-page">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} data-testid="admin-payouts-back"><ArrowLeft className="w-5 h-5" /></button>
            <h1 className="text-lg font-semibold">Driver payouts</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={load} data-testid="admin-payouts-refresh"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
            <Button size="sm" variant="outline" onClick={exportDigestCSV} disabled={!digest?.rows?.length} data-testid="admin-payouts-export-csv"><Download className="w-4 h-4 mr-1" />Weekly CSV</Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 pt-5 space-y-6">
        {digest && (
          <section data-testid="digest-totals" className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <StatCard label="Drivers this week" value={digest.totals?.total_drivers ?? 0} />
            <StatCard label="Period earnings" value={fmtJMD(digest.totals?.period_earnings_total_jmd)} />
            <StatCard label="Period paid" value={fmtJMD(digest.totals?.period_paid_total_jmd)} />
            <StatCard label="Total available" value={fmtJMD(digest.totals?.available_total_jmd)} />
          </section>
        )}

        <section className="bg-white rounded-xl border">
          <div className="p-4 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h2 className="font-semibold">Withdrawal requests</h2>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={status} onChange={(e) => setStatus(e.target.value)}
                className="rounded border px-2 py-1 text-sm" data-testid="admin-payouts-status-filter"
              >
                <option value="">All</option>
                {['requested', 'approved', 'paid', 'rejected', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
            ) : withdrawals.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500">No requests</div>
            ) : (
              <table className="w-full text-sm" data-testid="admin-payouts-table">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    {['Driver', 'Amount', 'Status', 'Requested', 'Paid', 'Method', 'Reference', 'Actions'].map(h => (
                      <th key={h} className="text-left p-3 font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {withdrawals.map(w => (
                    <tr key={w.id} className="hover:bg-gray-50" data-testid={`payout-row-${w.id}`}>
                      <td className="p-3 whitespace-nowrap">{w.driver_id}</td>
                      <td className="p-3 font-semibold whitespace-nowrap">{fmtJMD(w.approved_amount || w.requested_amount)}</td>
                      <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[w.status] || 'bg-gray-100'}`}>{w.status}</span></td>
                      <td className="p-3 whitespace-nowrap">{fmtDate(w.requested_at)}</td>
                      <td className="p-3 whitespace-nowrap">{fmtDate(w.paid_at)}</td>
                      <td className="p-3 whitespace-nowrap">{w.payment_method}</td>
                      <td className="p-3 whitespace-nowrap">{w.payment_reference || '—'}</td>
                      <td className="p-3 flex gap-1 whitespace-nowrap">
                        {w.status === 'requested' && (
                          <>
                            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" disabled={actioning === `${w.id}:approve`} onClick={() => act(w.id, 'approve')} data-testid={`approve-${w.id}`}><CheckCircle className="w-3.5 h-3.5 mr-1" />Approve</Button>
                            <Button size="sm" variant="outline" className="text-red-600 border-red-300" disabled={actioning === `${w.id}:reject`} onClick={() => act(w.id, 'reject')} data-testid={`reject-${w.id}`}><XCircle className="w-3.5 h-3.5 mr-1" />Reject</Button>
                          </>
                        )}
                        {w.status === 'approved' && (
                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={actioning === `${w.id}:mark-paid`} onClick={() => act(w.id, 'mark-paid')} data-testid={`mark-paid-${w.id}`}><CreditCard className="w-3.5 h-3.5 mr-1" />Mark paid</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Collision triage section */}
        <section className="bg-white rounded-xl border" data-testid="collision-triage-section">
          <div className="p-4 border-b flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <h2 className="font-semibold">Identity collision triage</h2>
              <span className="text-xs text-gray-500">{collisions.length} unreviewed</span>
            </div>
            <Button size="sm" variant="outline" onClick={exportCollisionsCSV} disabled={!collisions.length} data-testid="collisions-export-csv"><Download className="w-4 h-4 mr-1" />CSV</Button>
          </div>
          <div className="overflow-x-auto">
            {collisions.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">No collisions pending review</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>{['Collection', 'Record', 'Name', 'Phone', 'Blocked by', 'Activity', 'Hint', 'Detected', 'Action'].map(h => (<th key={h} className="text-left p-3 font-medium">{h}</th>))}</tr>
                </thead>
                <tbody className="divide-y">
                  {collisions.map(c => (
                    <tr key={c.id} className="hover:bg-gray-50" data-testid={`collision-row-${c.id}`}>
                      <td className="p-3">{c.subject?.collection}</td>
                      <td className="p-3 font-mono text-xs">{c.subject?.id}</td>
                      <td className="p-3">{c.display_name || '—'}</td>
                      <td className="p-3 font-mono text-xs">{c.normalized_phone}</td>
                      <td className="p-3 text-xs">{(c.after?.blocking_holders || []).map(h => `${h.collection}:${h.id}`).join(', ')}</td>
                      <td className="p-3 text-xs">{JSON.stringify(c.after?.activity_signal || {}).slice(0, 60)}</td>
                      <td className="p-3 text-xs">{c.recommendation_hint || 'review_manually'}</td>
                      <td className="p-3 text-xs whitespace-nowrap">{fmtDate(c.at)}</td>
                      <td className="p-3"><Button size="sm" variant="outline" onClick={() => markReviewed(c.id)} data-testid={`mark-reviewed-${c.id}`}>Mark reviewed</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-xl bg-white border p-4">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-xl font-bold mt-1">{value}</div>
    </div>
  );
}
